import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import OrgChurnStatus, { STATUS_EXPLANATIONS } from "../../shared/OrgChurnStatus";
import ChurnLegend from "../../shared/ChurnLegend";
import {
  CHURN_EVALUATED_STATUSES,
  CONFIG_SOURCE_DESCRIPTIONS,
  CONFIG_SOURCE_LABELS,
  ELIGIBILITY_REASON_LABELS,
  formatChurnActivityPhrase,
  formatChurnDateTime,
  formatChurnLoginPhrase,
  getChurnStatusMeta,
} from "../../shared/orgChurn";
import {
  createOrgChurnConfigMutation,
  deleteOrgChurnConfigMutation,
  orgChurnDetailsQuery,
  updateOrgChurnConfigMutation,
} from "../queries/orgChurn";

const { Text } = Typography;

const ORG_CHURN_CONFIG_SCHEMA_VERSION = 1;

const getEdges = (connection) =>
  connection?.edges?.map((edge) => edge.node).filter(Boolean) || [];

const formatDays = (value) => ((value ?? null) === null ? "-" : `${value} gg`);

// Tinta della card in base alla gravità: stesso colore del tag/badge dello
// stato, reso come bordo + sfondo tenue (token antd corrispondenti).
const STATUS_CARD_STYLE = {
  red: { border: "#ffa39e", background: "#fff1f0" },
  orange: { border: "#ffd591", background: "#fff7e6" },
  gold: { border: "#ffe58f", background: "#fffbe6" },
  purple: { border: "#d3adf7", background: "#f9f0ff" },
  default: { border: "#d9d9d9", background: "#fafafa" },
};

// Riga-frase con pallino: rosso quando il segnale ha superato la soglia.
const StatusLine = ({ critical, children }) => (
  <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
    <span
      style={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        backgroundColor: critical ? "#f5222d" : "#52c41a",
        flexShrink: 0,
        transform: "translateY(1px)",
      }}
    />
    <span style={{ fontSize: 15 }}>{children}</span>
  </div>
);

const ChurnFooter = ({ indicator }) => {
  const thresholds = [];
  if (indicator.login_inactivity_days != null) {
    thresholds.push(`login ${formatDays(indicator.login_inactivity_days)}`);
  }
  if (indicator.creation_inactivity_days != null) {
    thresholds.push(`attività ${formatDays(indicator.creation_inactivity_days)}`);
  }

  const sourceLabel = CONFIG_SOURCE_LABELS[indicator.config_source || "none"];
  const recomputed = [];
  if (indicator.computed_at) {
    recomputed.push(
      `ricalcolato il ${formatChurnDateTime(indicator.computed_at)}`
    );
  }
  if (sourceLabel) {
    recomputed.push(sourceLabel.toLowerCase());
  }

  return (
    <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 8 }}>
      {thresholds.length > 0 && (
        <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
          Soglie: {thresholds.join(" · ")}
        </Text>
      )}
      {recomputed.length > 0 && (
        <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
          {recomputed.join(" · ")}
        </Text>
      )}
    </div>
  );
};

// Sintesi essenziale a frasi dirette: due righe di stato (login / attività)
// con pallino colore, più un footer con soglie e momento del ricalcolo.
const ChurnSummary = ({ indicator }) => {
  if (!CHURN_EVALUATED_STATUSES.includes(indicator.status)) {
    const reason =
      !indicator.eligible && indicator.eligibility_reason
        ? ELIGIBILITY_REASON_LABELS[indicator.eligibility_reason] ||
          indicator.eligibility_reason
        : null;

    return (
      <Space direction="vertical" size={6} style={{ width: "100%" }}>
        <Text>
          {STATUS_EXPLANATIONS[indicator.status] ||
            "Stato churn non valutato per questa organizzazione."}
        </Text>
        {reason && <Text type="secondary">Motivo: {reason}</Text>}
        <ChurnFooter indicator={indicator} />
      </Space>
    );
  }

  const loginCritical =
    indicator.login_inactive || indicator.days_since_last_login == null;
  const activityCritical =
    indicator.creation_inactive ||
    indicator.days_since_last_activity_creation == null;

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <StatusLine critical={loginCritical}>
          {formatChurnLoginPhrase(indicator)}
        </StatusLine>
        <StatusLine critical={activityCritical}>
          {formatChurnActivityPhrase(indicator)}
        </StatusLine>
      </Space>
      <ChurnFooter indicator={indicator} />
    </Space>
  );
};

const OrgChurnPanel = ({ org }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [indicator, setIndicator] = useState(null);
  const [orgConfig, setOrgConfig] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(null);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const apiBaseUrl = config.baseUrl[env];
  const graphqlUrl = `${apiBaseUrl}/graphql?raw=true`;
  const churnRecomputeUrl = org?.objectId
    ? `${apiBaseUrl}/orgs/${org.objectId}/churn/recompute`
    : null;

  const effectiveConfig = orgConfig || globalConfig;
  const hasOverride = !!orgConfig;

  const fetchChurnDetails = useCallback(async ({ recompute = false } = {}) => {
    if (!org?.objectId) return;

    setLoading(true);
    try {
      const token = await getTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      if (recompute && churnRecomputeUrl) {
        await axios({
          method: "post",
          url: churnRecomputeUrl,
          headers,
        });
      }

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: orgChurnDetailsQuery,
          variables: { orgId: org.objectId },
        },
        headers,
      });

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "GraphQL error");
      }

      const indicators = getEdges(response.data?.org_churn_indicators);
      const configs = getEdges(response.data?.org_churn_configs);
      const nextOrgConfig =
        configs.find(
          (item) =>
            item.scope === "org" && item.org_pointer?.objectId === org.objectId
        ) || null;
      const nextGlobalConfig =
        configs.find(
          (item) => item.scope === "global" && item.config_key === "global"
        ) || null;
      const nextEffectiveConfig = nextOrgConfig || nextGlobalConfig;

      setIndicator(indicators[0] || null);
      setOrgConfig(nextOrgConfig);
      setGlobalConfig(nextGlobalConfig);
      form.setFieldsValue({
        enabled: nextEffectiveConfig?.enabled !== false,
        login_inactivity_days: nextEffectiveConfig?.login_inactivity_days,
        creation_inactivity_days: nextEffectiveConfig?.creation_inactivity_days,
      });
    } catch (error) {
      console.error("Error fetching org churn details", error);
      message.error("Errore nel caricamento dei dati churn");
    } finally {
      setLoading(false);
    }
  }, [churnRecomputeUrl, form, getTokenSilently, graphqlUrl, org?.objectId]);

  useEffect(() => {
    fetchChurnDetails();
  }, [fetchChurnDetails]);

  useEffect(() => {
    if (!configModalOpen) return;

    form.setFieldsValue({
      enabled: effectiveConfig?.enabled !== false,
      login_inactivity_days: effectiveConfig?.login_inactivity_days,
      creation_inactivity_days: effectiveConfig?.creation_inactivity_days,
    });
  }, [configModalOpen, effectiveConfig, form]);

  const configSourceLabel = useMemo(() => {
    if (orgConfig) return CONFIG_SOURCE_LABELS.org;
    if (globalConfig) return CONFIG_SOURCE_LABELS.global;
    return CONFIG_SOURCE_LABELS.none;
  }, [globalConfig, orgConfig]);

  const configSourceDescription = useMemo(() => {
    if (orgConfig) return CONFIG_SOURCE_DESCRIPTIONS.org;
    if (globalConfig) return CONFIG_SOURCE_DESCRIPTIONS.global;
    return CONFIG_SOURCE_DESCRIPTIONS.none;
  }, [globalConfig, orgConfig]);

  const saveOverride = async (values) => {
    setSaving(true);

    try {
      const token = await getTokenSilently();
      const fields = {
        scope: "org",
        config_key: `org:${org.objectId}`,
        org_pointer: { link: org.objectId },
        enabled: values.enabled !== false,
        login_inactivity_days: values.login_inactivity_days,
        creation_inactivity_days: values.creation_inactivity_days,
      };
      const mutation = orgConfig
        ? updateOrgChurnConfigMutation
        : createOrgChurnConfigMutation;
      const variables = orgConfig
        ? { id: orgConfig.objectId, fields }
        : {
            fields: {
              ...fields,
              schema_version: ORG_CHURN_CONFIG_SCHEMA_VERSION,
            },
          };

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: { query: mutation, variables },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "GraphQL error");
      }

      await fetchChurnDetails({ recompute: true });
      message.success("Override churn salvato e ricalcolato");
      setConfigModalOpen(false);
    } catch (error) {
      console.error("Error saving churn config override", error);
      message.error("Errore nel salvataggio o ricalcolo dell'override churn");
    } finally {
      setSaving(false);
    }
  };

  const deleteOverride = async () => {
    if (!orgConfig?.objectId) return;

    setSaving(true);
    try {
      const token = await getTokenSilently();
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: deleteOrgChurnConfigMutation,
          variables: { id: orgConfig.objectId },
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "GraphQL error");
      }

      await fetchChurnDetails({ recompute: true });
      message.success("Override churn rimosso e ricalcolato");
      setConfigModalOpen(false);
    } catch (error) {
      console.error("Error deleting churn config override", error);
      message.error("Errore nella rimozione o ricalcolo dell'override churn");
    } finally {
      setSaving(false);
    }
  };

  // Sulle org sane non mostriamo nulla: il pannello compare solo quando c'è un
  // segnale churn da attenzionare. Durante il primo caricamento restiamo
  // invisibili, per non far lampeggiare il box sulle org OK.
  if (loading && !indicator) {
    return null;
  }
  if (indicator && indicator.status === "ok") {
    return null;
  }

  const statusMeta = indicator ? getChurnStatusMeta(indicator.status) : null;
  const cardStyle =
    STATUS_CARD_STYLE[statusMeta?.color] || STATUS_CARD_STYLE.default;

  return (
    <>
      <Card
        title={
          <Space wrap>
            <span>Rischio churn</span>
            <OrgChurnStatus indicator={indicator} />
            <ChurnLegend />
          </Space>
        }
        extra={
          <Space wrap>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => fetchChurnDetails({ recompute: true })}
              loading={loading}
            >
              Aggiorna
            </Button>
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => setConfigModalOpen(true)}
            >
              Configura soglie
            </Button>
          </Space>
        }
        bordered={false}
        style={{
          backgroundColor: cardStyle.background,
          border: `1px solid ${cardStyle.border}`,
        }}
      >
        {!indicator ? (
          <Alert
            type="warning"
            showIcon
            message="Indicatore churn non ancora calcolato"
            description="La Lambda Mission Control non ha ancora scritto un indicatore per questa organizzazione."
          />
        ) : (
          <ChurnSummary indicator={indicator} />
        )}
      </Card>

      <Modal
        title="Configura soglie churn"
        open={configModalOpen}
        onCancel={() => setConfigModalOpen(false)}
        footer={null}
        destroyOnClose={false}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Text type="secondary">Soglie applicate</Text>
            <div style={{ fontWeight: 600 }}>{configSourceLabel}</div>
            <Text type="secondary">{configSourceDescription}</Text>
          </div>

          {!globalConfig && !orgConfig && (
            <Alert
              type="warning"
              showIcon
              message="Config churn globale mancante"
            />
          )}

          {effectiveConfig && (
            <Alert
              type="info"
              showIcon
              message={`Soglie effettive: login ${effectiveConfig.login_inactivity_days} giorni, attività ${effectiveConfig.creation_inactivity_days} giorni.`}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={saveOverride}
            disabled={saving}
          >
            <Form.Item
              name="enabled"
              label="Calcolo abilitato"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="login_inactivity_days"
              label="Giorni inattività login"
              rules={[{ required: true, message: "Campo obbligatorio" }]}
            >
              <InputNumber min={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="creation_inactivity_days"
              label="Giorni inattività attività"
              rules={[{ required: true, message: "Campo obbligatorio" }]}
            >
              <InputNumber min={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>

            <Space wrap>
              <Button type="primary" htmlType="submit" loading={saving}>
                Salva override
              </Button>
              <Popconfirm
                title="Rimuovere l'override e tornare alle soglie standard?"
                okText="Rimuovi override"
                cancelText="Annulla"
                disabled={!hasOverride || saving}
                onConfirm={deleteOverride}
              >
                <Button disabled={!hasOverride || saving}>
                  Rimuovi override soglie
                </Button>
              </Popconfirm>
              <Button onClick={() => setConfigModalOpen(false)}>
                Annulla
              </Button>
            </Space>
          </Form>
        </Space>
      </Modal>
    </>
  );
};

export default OrgChurnPanel;
